// /src/pages/private/admin/AdminPanel.jsx

import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSecurity } from '../../../SecurityContext';
import LoadingSpinner from '../../../components/LoadingSpinner';
import AdminSideBar from '../../../components/admin/AdminSideBar';

const Dashboard = lazy(() => import('./Dashboard'));
const SiteAdmin = lazy(() => import('./SiteAdmin'));
const Onboarding = lazy(() => import('./Onboarding'));
const Organization = lazy(() => import('./Organization'));
const OrganisationMembers = lazy(() => import('./OrganisationMembers'));
const Accounting = lazy(() => import('./Accounting'));
const PublicPages = lazy(() => import('./PublicPages'));
const PrivateDocuments = lazy(() => import('./PrivateDocuments'));
const MembersPerks = lazy(() => import('./MembersPerks'));
const EventManagement = lazy(() => import('./EventManagement'));
const OrgStructure = lazy(() => import('./OrgStructure'));
const Academy = lazy(() => import('./Academy'));
const Alliances = lazy(() => import('./Alliances'));
const Images = lazy(() => import('./Images'));

//  generic permission gate to avoid premature redirects
function RequirePermission({ resource, action = 'read', children }) {
  const { loading, userHasPermission, permissionsVersion } = useSecurity();

  //  wait for security context to fully initialize
  if (loading || permissionsVersion === 0) {
    return <LoadingSpinner />;
  }

  const hasPerm = userHasPermission(resource, action);
  if (!hasPerm) {
    console.warn(`[RequirePermission] Missing permission: ${resource}.${action}`);
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

export default function AdminPanel() {
  const { t } = useTranslation();
  const { loading, userHasPermission } = useSecurity();

  //  don't evaluate any permission until security context is ready
  if (loading) return <LoadingSpinner />;

  return (
    <RequirePermission resource="admin" action="read">
      <div className="admin-panel-root" data-resource="admin" data-action="read" data-description="Admin">
        <div className="user-admin-page">
          <div className="admin-container">
            <AdminSideBar userHasPermission={userHasPermission} />
            <div className="user-admin-content-wrapper glass-card2">
              <div className="user-admin-content">
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* dashboard */}
                    <Route
                      index
                      element={
                      <RequirePermission resource="admin.dashboard" action="read">
                          <Navigate to="/admin/dashboard" replace />
                        </RequirePermission>
                      } />
                    
                    <Route
                      path="dashboard/:tab?"
                      element={
                      <RequirePermission resource="admin.dashboard" action="read">
                          <Dashboard />
                        </RequirePermission>
                      } />
                    

                    {/* site admin */}
                    <Route
                      path="siteadmin/:tab?"
                      element={
                      <RequirePermission resource="admin.siteadmin" action="write">
                          <SiteAdmin />
                        </RequirePermission>
                      } />
                    
                    {/* onboarding */}
                    <Route
                      path="onboarding/:tab?"
                      element={
                      <RequirePermission resource="admin.onboarding" action="read">
                          <Onboarding />
                        </RequirePermission>
                      } />
                    

                    {/* organization */}
                    <Route
                      path="organization/:tab?"
                      element={
                      <RequirePermission resource="admin.organization" action="read">
                          <Organization />
                        </RequirePermission>
                      } />

                    {/* organisation members */}
                    <Route
                      path="organisation-members/:tab?"
                      element={
                      <RequirePermission resource="admin.organisationMembers" action="read">
                          <OrganisationMembers />
                        </RequirePermission>
                      } />

                    {/* members perks */}
                    <Route
                      path="members-perks/:tab?"
                      element={
                      <RequirePermission resource="admin.membersperks" action="read">
                          <MembersPerks />
                        </RequirePermission>
                    } />


                    {/* accounting */}
                    <Route
                      path="accounting/:tab?"
                      element={
                      <RequirePermission resource="admin.accounting" action="read">
                          <Accounting />
                        </RequirePermission>
                      } />
                    

                    {/* public pages */}
                    <Route
                      path="publicpages/:tab?"
                      element={
                      <RequirePermission resource="admin.publicpages" action="read">
                          <PublicPages />
                        </RequirePermission>
                      } />
                    
                    {/* private documents */}
                    <Route
                      path="privatedocuments/:tab?"
                      element={
                      <RequirePermission resource="admin.privatedocuments" action="read">
                          <PrivateDocuments isPrivate={true} />
                        </RequirePermission>
                      } />


                     {/* event management */}
                    <Route
                      path="eventmanagement/:tab?"
                      element={
                      <RequirePermission resource="admin.eventmanagement" action="read">
                          <EventManagement />
                        </RequirePermission>
                      } />
                    

                    {/* org structure */}
                    <Route
                      path="orgstructure/:tab?"
                      element={
                      <RequirePermission resource="admin.orgstructure" action="read">
                          <OrgStructure />
                        </RequirePermission>
                      } />
                    

                    {/* academy */}
                    <Route
                      path="academy/:tab?"
                      element={
                      <RequirePermission resource="admin.academy" action="read">
                          <Academy />
                        </RequirePermission>
                      } />
                    

                    {/* alliances */}
                    <Route
                      path="alliances/:tab?"
                      element={
                      <RequirePermission resource="admin.alliances" action="read">
                          <Alliances />
                        </RequirePermission>
                      } />
                    

                    {/* images */}
                    <Route
                      path="images/:tab?"
                      element={
                      <RequirePermission resource="admin.images" action="read">
                          <Images />
                        </RequirePermission>
                      } />
                    

                    {/* fallback */}
                    <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                  </Routes>
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </div>
    </RequirePermission>);

}